import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useAuth, type LocationData } from "@/contexts/AuthContext";
import {
  COLLAPSED_WIDTH,
  EXPANDED_WIDTH,
  HOVER_DELAY,
  MANY_LOCATIONS_THRESHOLD,
  RECENT_COUNT,
  RECENT_STORAGE_KEY,
  SNAP_UNIT,
} from "@/utils/constants";
import type { LocationSelectorProps } from "@/utils/types";
import { LocationGuidanceModal } from "@/components/LocationGuidanceModal/LocationGuidanceModal";
import styles from "./LocationSelector.module.css";

/**
 * Get initials for a location name.
 * Single word → first two letters (e.g. "Market" → "MA")
 * Multiple words → first letter of each word (e.g. "Deli Downtown" → "DD")
 */
function getInitials(name: string): string {
  const words = name.trim().split(/\s+/);
  if (words.length === 1) {
    return words[0].substring(0, 2).toUpperCase();
  }
  return words
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .substring(0, 2);
}

function loadRecentIds(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveRecentIds(ids: string[]): void {
  localStorage.setItem(RECENT_STORAGE_KEY, JSON.stringify(ids));
}

const OrgIcon: React.FC = () => (
  <svg width="21" height="21" viewBox="0 0 21 21" fill="none" aria-hidden="true">
    <path
      d="M0 0V21H8.54603V16.0521H12.1789V21H21V0H0ZM6.20384 12.3308H2.57101V8.66918H6.20384V12.3308ZM6.20384 6.5142H2.57101V2.84996H6.20384V6.5142ZM12.1789 12.3308H8.54603V8.66918H12.1789V12.3308ZM12.1789 6.5142H8.54603V2.84996H12.1789V6.5142ZM18.1539 12.3308H14.5211V8.66918H18.1539V12.3308ZM18.1539 6.5142H14.5211V2.84996H18.1539V6.5142Z"
      fill="currentColor"
    />
  </svg>
);

/**
 * Search icon (Figma "search", 28px)
 */
const SearchIcon: React.FC = () => (
  <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true">
    <path
      d="M12.833 3.5C17.9876 3.5 22.1668 7.6785 22.167 12.833C22.167 15.1642 21.3096 17.294 19.8965 18.9297L24.7422 23.7754L23.0918 25.4248L18.1611 20.4941C16.6501 21.547 14.8143 22.167 12.833 22.167C7.6785 22.1668 3.5 17.9876 3.5 12.833C3.50018 7.67861 7.67861 3.50018 12.833 3.5ZM12.833 5.83301C8.96727 5.83318 5.83318 8.96727 5.83301 12.833C5.83301 16.6989 8.96716 19.8328 12.833 19.833C16.699 19.833 19.833 16.699 19.833 12.833C19.8328 8.96716 16.6989 5.83301 12.833 5.83301Z"
      fill="currentColor"
    />
  </svg>
);

export const LocationSelector: React.FC<LocationSelectorProps> = ({
  isMobileMenuOpen = false,
  newLocationIds = [],
  showLocationGuidanceSteps = true,
  onLocationGuidanceComplete,
}) => {
  const { locations, selectedLocation, selectLocation } = useAuth();
  const navigate = useNavigate();

  const [isExpanded, setIsExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [recentIds, setRecentIds] = useState<string[]>(loadRecentIds);
  const [guidanceLocation, setGuidanceLocation] = useState<LocationData | null>(null);

  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);

  const hasManyLocations = locations.length > MANY_LOCATIONS_THRESHOLD;
  const newLocationIdSet = useMemo(() => new Set(newLocationIds), [newLocationIds]);

  // Recent locations (only when > 6 locations). The selected location is always
  // first so it is highlighted once, at the top of the list.
  const recentLocations = useMemo(() => {
    if (!hasManyLocations) return [];
    const selectedId = selectedLocation?.id;
    const stored = recentIds
      .map((id) => locations.find((l) => l.id === id))
      .filter((l): l is LocationData => Boolean(l) && l?.id !== selectedId);
    const selected = selectedId
      ? locations.find((l) => l.id === selectedId)
      : undefined;
    return (selected ? [selected, ...stored] : stored).slice(0, RECENT_COUNT);
  }, [recentIds, locations, hasManyLocations, selectedLocation]);

  // Filtered locations for search
  const filteredLocations = useMemo(() => {
    if (!searchQuery.trim()) return locations;
    const q = searchQuery.toLowerCase();
    return locations.filter((l) => l.name.toLowerCase().includes(q));
  }, [locations, searchQuery]);

  // The selected location leads the recents section when there are many
  // locations, so it is left out of the full list. Otherwise it moves to the top.
  const listLocations = useMemo(() => {
    if (!selectedLocation) return filteredLocations;
    if (hasManyLocations) {
      return filteredLocations.filter((l) => l.id !== selectedLocation.id);
    }
    const selected = filteredLocations.find((l) => l.id === selectedLocation.id);
    if (!selected) return filteredLocations;
    return [selected, ...filteredLocations.filter((l) => l.id !== selected.id)];
  }, [filteredLocations, hasManyLocations, selectedLocation]);

  // Handle hover enter with delay
  const handleMouseEnter = useCallback(() => {
    hoverTimerRef.current = setTimeout(() => {
      setIsExpanded(true);
    }, HOVER_DELAY);
  }, []);

  // Handle hover leave — immediate collapse
  const handleMouseLeave = useCallback(() => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    setIsExpanded(false);
  }, []);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    };
  }, []);

  // Force expanded on tablet when mobile menu is open
  useEffect(() => {
    const checkTabletState = () => {
      const isTablet = window.innerWidth >= 768 && window.innerWidth <= 1024;
      if (isTablet && isMobileMenuOpen) {
        setIsExpanded(true);
      } else if (isTablet && !isMobileMenuOpen) {
        setIsExpanded(false);
      }
    };

    // Check on mount and when isMobileMenuOpen changes
    checkTabletState();

    // Listen for resize events to handle viewport changes
    let resizeTimer: ReturnType<typeof setTimeout>;
    const handleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(checkTabletState, 150);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout(resizeTimer);
    };
  }, [isMobileMenuOpen]);

  // Focus search input when expanded (if many locations)
  useEffect(() => {
    if (isExpanded && hasManyLocations && searchInputRef.current) {
      // Small delay to let animation start
      const t = setTimeout(() => searchInputRef.current?.focus(), 100);
      return () => clearTimeout(t);
    }
  }, [isExpanded, hasManyLocations]);

  /**
   * Update recent locations: push selected to top, keep 3 max
   */
  const updateRecents = useCallback((location: LocationData) => {
    setRecentIds((prev) => {
      const filtered = prev.filter((id) => id !== location.id);
      const updated = [location.id, ...filtered].slice(0, RECENT_COUNT);
      saveRecentIds(updated);
      return updated;
    });
  }, []);

  /**
   * Handle selecting a location
   */
  const handleSelectLocation = useCallback(
    (location: LocationData) => {
      if (newLocationIdSet.has(location.id)) {
        selectLocation(location);
        if (hasManyLocations) {
          updateRecents(location);
        }
        if (searchQuery) {
          setSearchQuery("");
        }
        setGuidanceLocation(location);
        navigate("/camera");
        return;
      }
      selectLocation(location);
      if (hasManyLocations) {
        updateRecents(location);
      }
      // Clear search after selection
      if (searchQuery) {
        setSearchQuery("");
      }
      // Navigate to Live Cameras on location change
      navigate("/camera");
    },
    [newLocationIdSet, selectLocation, hasManyLocations, updateRecents, searchQuery, navigate],
  );

  const handleNewBadgeClick = useCallback((location: LocationData) => {
    setGuidanceLocation(location);
  }, []);

  /**
   * Handle selecting "My Organization" (null location = org level)
   * Navigate to /camera.
   */
  const handleSelectOrg = useCallback(() => {
    selectLocation(null);
    if (searchQuery) {
      setSearchQuery("");
    }
    navigate("/camera");
  }, [selectLocation, searchQuery, navigate]);

  /**
   * Snap scroll handler
   */
  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    // debounced snap — on scroll end, snap to nearest item
    const timer = setTimeout(() => {
      const offset = el.scrollTop;
      const snapped = Math.round(offset / SNAP_UNIT) * SNAP_UNIT;
      if (Math.abs(offset - snapped) > 2) {
        el.scrollTo({ top: snapped, behavior: "smooth" });
      }
    }, 150);
    return () => clearTimeout(timer);
  }, []);

  // Spring animation config for width
  const springTransition = {
    type: "spring" as const,
    stiffness: 350,
    damping: 32,
    mass: 0.8,
  };

  // Must run before the early return below — a hook can't be skipped on some
  // renders (e.g. when `locations` becomes empty) without React complaining
  // about a changed hook count.
  const { t } = useTranslation();

  if (locations.length === 0) {
    return null;
  }

  const isOrgSelected = selectedLocation === null;

  const renderLocationRow = (
    loc: LocationData,
    keyPrefix: string,
    highlighted: boolean,
  ) => {
    // A layoutId must be unique among mounted rows; recents also appear in the full list.
    const isDuplicated =
      recentLocations.some((l) => l.id === loc.id) &&
      listLocations.some((l) => l.id === loc.id);
    return (
    <motion.div
      key={`${keyPrefix}-${loc.id}`}
      layout="position"
      layoutId={isDuplicated ? undefined : `location-${loc.id}`}
      transition={springTransition}
      className={styles.locationRow}
    >
      <button
        className={`${styles.locationItem} ${highlighted ? styles.selected : ""}`}
        onClick={() => handleSelectLocation(loc)}
        title={loc.name}
      >
        {!isExpanded && (
          <div className={styles.initialsBox}>{getInitials(loc.name)}</div>
        )}
        {isExpanded && (
          <motion.span
            className={styles.locationNameFull}
            initial={{ opacity: 0, filter: "blur(4px)" }}
            animate={{ opacity: 1, filter: "blur(0px)" }}
            exit={{ opacity: 0, filter: "blur(4px)" }}
            transition={{ duration: 0.2 }}
          >
            {loc.name}
          </motion.span>
        )}
      </button>
      {newLocationIdSet.has(loc.id) && (
        <button
          type="button"
          className={styles.newBadge}
          onClick={() => handleNewBadgeClick(loc)}
          aria-label={`${t('locationSelector.new')} ${loc.name}`}
        >
          {t('locationSelector.new')}
        </button>
      )}
    </motion.div>
    );
  };

  return (
    <motion.aside
      ref={sidebarRef}
      className={`${styles.sidebar} ${isExpanded ? styles.expanded : ""} ${isMobileMenuOpen ? styles.sidebarMobileOpen : ""}`}
      initial={false}
      animate={{ width: isExpanded ? EXPANDED_WIDTH : COLLAPSED_WIDTH }}
      transition={springTransition}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className={styles.content}>
        {/* Organization Button (sticky) */}
        <div className={styles.stickySection}>
          <button
            className={`${styles.locationItem} ${styles.orgButton} ${isOrgSelected ? styles.selected : ""}`}
            onClick={handleSelectOrg}
            title={t('locationSelector.myOrganization')}
          >
            <div className={styles.orgIconBox}>
              <OrgIcon />
            </div>
            <AnimatePresence>
              {isExpanded && (
                <motion.span
                  className={styles.locationName}
                  initial={{ opacity: 0, filter: "blur(4px)" }}
                  animate={{ opacity: 1, filter: "blur(0px)" }}
                  exit={{ opacity: 0, filter: "blur(4px)" }}
                  transition={{ duration: 0.2 }}
                >
                  {t('locationSelector.myOrganization')}
                </motion.span>
              )}
            </AnimatePresence>
          </button>

          <div className={styles.divider} aria-hidden="true" />

          {/* Recent Locations (only for >6 locations, sticky) */}
          {hasManyLocations && recentLocations.length > 0 && (
            <>
              <div className={styles.recentGroup}>
                {recentLocations.map((loc) =>
                  renderLocationRow(loc, "recent", selectedLocation?.id === loc.id),
                )}
              </div>
              <div className={styles.divider} aria-hidden="true" />
            </>
          )}

          {/* Search (sticky, only for >6 locations) */}
          {hasManyLocations && (
            <div className={styles.searchContainer}>
              {isExpanded ? (
                <div className={styles.searchInputWrapper}>
                  <SearchIcon />
                  <input
                    ref={searchInputRef}
                    className={styles.searchInput}
                    type="text"
                    placeholder="Search"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  {searchQuery && (
                    <button
                      className={styles.searchClear}
                      onClick={() => setSearchQuery("")}
                      aria-label="Clear search"
                    >
                      ✕
                    </button>
                  )}
                </div>
              ) : (
                <button
                  className={styles.searchCollapsed}
                  title={t('locationSelector.searchLocations')}
                >
                  <SearchIcon />
                </button>
              )}
            </div>
          )}
        </div>

        {/* All Locations (scrollable) */}
        <div
          ref={scrollRef}
          className={styles.scrollableSection}
          onScroll={handleScroll}
        >
          {listLocations.map((loc) =>
            renderLocationRow(
              loc,
              "all",
              !hasManyLocations && selectedLocation?.id === loc.id,
            ),
          )}
          {filteredLocations.length === 0 && searchQuery && (
            <div className={styles.noResults}>{t('locationSelector.noLocationsFound')}</div>
          )}
        </div>
      </div>

      {/* Fade mask at bottom */}
      <div className={styles.fadeMask} />
      {guidanceLocation && (
        <LocationGuidanceModal
          locationId={guidanceLocation.id}
          locationName={guidanceLocation.name}
          showGuidanceSteps={showLocationGuidanceSteps}
          onClose={() => setGuidanceLocation(null)}
          onContinue={() => {
            void (async () => {
              await onLocationGuidanceComplete?.(guidanceLocation.id);
              setGuidanceLocation(null);
              navigate("/camera");
            })();
          }}
        />
      )}
    </motion.aside>
  );
};

export default LocationSelector;
